import { ClickAwayListener } from "@mui/base";
import { Alert, Divider, Grid2 as Grid, IconButton } from "@mui/material";
import { UnitType as UnitTypeEnum } from "@sinch/core/entities/serverEnums";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsClose } from "@sinch/icons";
import type { UnitType } from "@sinch/types/sinch.types";
import { mergeDeepRight, pick } from "ramda";
import { isNotUndefined, isNull, isUndefined } from "ramda-adjunct";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useUnitType } from "@sinch/components/application/Rate/useUnitType";
import {
  IntervalPopoverPaper,
  IntervalPopper,
  IntervalSettingsGrid,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/components/styled";
import {
  IntervalAdditionalSettings,
  IntervalAdditionalSettingsValue,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/IntervalPopover/IntervalAdditionalSettings";
import { IntervalMultilevelSelect } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/IntervalPopover/IntervalMultilevelSelect";
import {
  keepsRoundingOnUnitChange,
  roundingKindOfUnit,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/IntervalPopover/RoundingInput";
import { RateIntervals } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.graphql";
import { RateIntervalsQuery } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.types";
import type {
  IntervalOption,
  IntervalSelectValue,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/types";
import { rateRateSchema } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";

/**
 * Popover component for interval selection with additional settings
 */
export const IntervalPopover: FC<{
  onChange: (value: IntervalSelectValue | null) => void;
  value: IntervalSelectValue | null;
  anchorEl: HTMLDivElement | null;
  open: boolean;
  onClickAway: (event: MouseEvent | TouchEvent) => void;
  onToggleOpen: React.MouseEventHandler<HTMLDivElement | HTMLButtonElement>;
  options: IntervalOption[];
}> = ({ onChange, value, options, anchorEl, open, onClickAway, onToggleOpen }) => {
  const { t } = useTranslation(["common", "draft"]);
  const { rateType } = useRateBuilderSettings();
  const { refetch } = useQueryHandler<RateIntervalsQuery>(RateIntervals, { ratelistType: rateType });
  const { getUnitName } = useUnitType(rateType);

  const modifyValue = useMemo(() => createValueModifier(value), [value]);
  const currentUnitType = value?.unit.type ?? null;

  /**
   * Switch the unit of the rule. A rule-level rounding is bound to the kind of unit it rounds, so it is
   * dropped when the new unit is rounded by another kind (or not at all) instead of lingering invisibly
   */
  const changeUnit = useCallback(
    (unit: { type: UnitType | UnitTypeEnum | null; customIntervalId: string | null }) => {
      const previousUnitName = currentUnitType ? getUnitName(currentUnitType) : null;
      const nextUnitName = unit.type ? getUnitName(unit.type as UnitType) : null;
      onChange(modifyValue(keepsRoundingOnUnitChange(previousUnitName, nextUnitName) ? { unit } : { unit, roundingId: null }));
    },
    [currentUnitType, getUnitName, modifyValue, onChange]
  );

  const handleAddCustomInterval = useCallback(
    (newRateIntervalId?: string) => {
      if (!newRateIntervalId) {
        return;
      }
      changeUnit({ type: UnitTypeEnum.Custom, customIntervalId: newRateIntervalId });
      refetch();
    },
    [changeUnit, refetch]
  );

  const handleUnitChange = useCallback<
    (value: { type: UnitType | UnitTypeEnum | null; customIntervalId: string | null } | null) => void
  >(
    (val) => {
      if (val?.type === UnitTypeEnum.Flat) {
        onChange(createValueModifier(null)({ unit: val }));
      } else if (isNull(val)) {
        onChange(null);
      } else {
        changeUnit(val);
      }
    },
    [changeUnit, onChange]
  );

  const shouldShowAdditionalSettings = value?.unit.type && value?.unit.type !== UnitTypeEnum.Flat;
  const unitSelectValue =
    isNotUndefined(value?.unit) && value?.unit ? pick(["type", "customIntervalId"], value.unit) : null;

  return (
    <IntervalPopper
      anchorEl={anchorEl}
      disablePortal
      modifiers={[
        {
          // position with top/left instead of a transform: a transformed popper would become the containing
          // block of the fixed-positioned menus of the selects rendered inside it (disablePortal) and misplace them
          name: "computeStyles",
          options: { gpuAcceleration: false },
        },
        {
          name: "flip",
          enabled: false,
          options: {
            altBoundary: true,
            rootBoundary: "document",
            padding: 8,
          },
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: "viewport",
            padding: 8,
          },
        },
      ]}
      open={open}
      slotProps={{ root: { "data-cy": "IntervalSelect-popover" } }}
    >
      <ClickAwayListener onClickAway={onClickAway}>
        <IntervalPopoverPaper elevation={1}>
          <Grid sx={{ zIndex: 1, position: "absolute", justifySelf: "end", right: 0 }}>
            <IconButton onClick={(e) => onToggleOpen(e)}>
              <MdsIcon fontSize="xs" icon={MdsClose} />
            </IconButton>
          </Grid>
          <Grid container direction="column">
            <Grid
              sx={{
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <IntervalMultilevelSelect
                onAddCustomInterval={handleAddCustomInterval}
                onChange={handleUnitChange}
                options={options}
                value={unitSelectValue}
              />
            </Grid>
            <Divider sx={{ mb: 2 }} />
            {isNull(value?.unit.type) && (
              <IntervalSettingsGrid>
                <Alert severity="info" variant="outlined">
                  {t("RateBuilder.selectUnitForAdditionalSettings")}
                </Alert>
              </IntervalSettingsGrid>
            )}
            {shouldShowAdditionalSettings ? (
              <AdditionalSettings onChange={onChange} value={value} />
            ) : (
              (isUndefined(value?.unit) || value?.unit.type === UnitTypeEnum.Flat) && (
                <IntervalSettingsGrid>
                  <Alert severity="info" variant="outlined">
                    {t("RateBuilder.thisUnitHasNoAdditionalSettings")}
                  </Alert>
                </IntervalSettingsGrid>
              )
            )}
          </Grid>
        </IntervalPopoverPaper>
      </ClickAwayListener>
    </IntervalPopper>
  );
};
/**
 * A function to create and modify a value by merging it with default values.
 */
const createValueModifier = (value: IntervalSelectValue | null) => {
  const defaultValues = pick(["unit", "minimum", "roundingId"], rateRateSchema.getDefault());
  return mergeDeepRight(mergeDeepRight(defaultValues, value ?? {}));
};

const AdditionalSettings = ({
  onChange,
  value,
}: {
  onChange: (value: IntervalSelectValue | null) => void;
  value: IntervalSelectValue;
}) => {
  const { rateType } = useRateBuilderSettings();
  const { getUnitName } = useUnitType(rateType);

  const unitName = value?.unit.type ? getUnitName(value.unit.type) : null;
  const shouldShowAdditionalSettings = roundingKindOfUnit(unitName) !== null;

  const modifyValue = useMemo(() => createValueModifier(value), [value]);

  const handleTimeAdditionalSettings = useCallback<(val: IntervalAdditionalSettingsValue) => void>(
    (val) => {
      onChange(
        modifyValue({
          unit: { priority: val.unit.priority },
          minimum: { ...val.minimum },
          roundingId: val.roundingId,
        })
      );
    },
    [modifyValue, onChange]
  );

  if (!shouldShowAdditionalSettings) {
    return null;
  }

  return (
    <IntervalSettingsGrid>
      <IntervalAdditionalSettings onChange={handleTimeAdditionalSettings} unitName={unitName} value={value} />
    </IntervalSettingsGrid>
  );
};
